/*
 * CUSTOM CODE
 * Free shipping threshold: the "+ Free shipping" bubble and the cart's
 * "Add X more for free shipping" bar.
 *
 * Both are rendered in Liquid (snippets/custom-product-tax-message.liquid and
 * snippets/free-shipping-bar.liquid), which already compares the price or cart total
 * with the free shipping threshold (see snippets/free-shipping-threshold.liquid).
 *
 * The threshold is set in the store's base currency, while prices are in the currency
 * the customer is shopping in. Liquid has no access to exchange rates, so these elements
 * convert the threshold with the storefront rate and correct what Liquid rendered when
 * the two currencies differ.
 */

/* Whether the customer is shopping in another currency than the store's base currency. */
function freeShippingIsForeignCurrency(shopCurrency) {
  const currency = (window.Shopify && window.Shopify.currency) || {};
  return Boolean(currency.active && currency.active !== shopCurrency && parseFloat(currency.rate));
}

/* The threshold (in cents, base currency) converted to the currency the customer is shopping in. */
function freeShippingThreshold(threshold, shopCurrency) {
  threshold = parseFloat(threshold) || 0;
  if (!freeShippingIsForeignCurrency(shopCurrency)) return threshold;
  return threshold * parseFloat(window.Shopify.currency.rate);
}

if (!customElements.get('free-shipping-bubble')) {
  customElements.define(
    'free-shipping-bubble',
    class FreeShippingBubble extends HTMLElement {
      connectedCallback() {
        this.update();

        // The tax message is not part of the HTML that is re-rendered when a variant is selected.
        if (this.hasAttribute('data-follow-variant') && typeof subscribe === 'function') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const section = this.closest('.shopify-section');
            const variant = event.data && event.data.variant;
            if (!variant || !section || !section.id.endsWith(event.data.sectionId)) return;

            this.dataset.price = variant.price;
            this.update();
          });
        }
      }

      disconnectedCallback() {
        if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
      }

      get threshold() {
        return freeShippingThreshold(this.dataset.threshold, this.dataset.shopCurrency);
      }

      update() {
        const price = parseFloat(this.dataset.price);
        // No price to compare with, or no threshold set: the bubble always shows.
        if (isNaN(price) || !this.threshold) {
          this.hidden = false;
          return;
        }

        this.hidden = price < this.threshold;
      }
    }
  );
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define(
    'free-shipping-bar',
    class FreeShippingBar extends HTMLElement {
      connectedCallback() {
        // In the store's base currency Liquid has already rendered the right amount.
        if (!freeShippingIsForeignCurrency(this.dataset.shopCurrency)) return;

        const total = parseFloat(this.dataset.total) || 0;
        const remaining = freeShippingThreshold(this.dataset.threshold, this.dataset.shopCurrency) - total;
        const amount = this.querySelector('.free-shipping-bar__amount');

        this.hidden = remaining <= 0;
        if (remaining <= 0 || !amount) return;

        // Round up to whole cents so we never ask for less than what is actually needed.
        const value = Math.ceil(remaining) / 100;
        try {
          amount.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: window.Shopify.currency.active,
          }).format(value);
        } catch (error) {
          amount.textContent = `${value.toFixed(2)} ${window.Shopify.currency.active}`;
        }
      }
    }
  );
}

/*
 * Threshold amounts written in page text, e.g. "for purchases above €99" or "above 99 EUR".
 *
 * For customers shopping in another currency the converted sum is added in brackets
 * after the amount, e.g. "99 EUR (SEK 1,128)". Customers shopping in the store's
 * base currency see the text exactly as written. On a page priced in another currency a
 * literal base currency amount can only be hand-written copy, never a product price.
 */
function freeShippingConvertTextAmounts(root) {
  const config = window.freeShipping || {};
  if (!config.threshold || !freeShippingIsForeignCurrency(config.shopCurrency)) return;
  // Only the euro notations are recognised.
  if (config.shopCurrency !== 'EUR') return;

  const amount = String(config.threshold / 100).replace('.', '[.,]');
  const pattern = new RegExp(
    `(^|[^\\d.,])(€\\s?${amount}|${amount}\\s?€|${amount}\\s?EUR|EUR\\s?${amount})(?![\\d]|[.,]\\d)`
  );

  let converted;
  try {
    converted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify.currency.active,
      maximumFractionDigits: 0,
    }).format(freeShippingThreshold(config.threshold, config.shopCurrency) / 100);
  } catch (error) {
    return;
  }

  const skip = 'script, style, textarea, input, select, [contenteditable], free-shipping-bar, [data-free-shipping-converted]';
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (pattern.test(node.nodeValue) && node.parentElement && !node.parentElement.closest(skip)) nodes.push(node);
  }

  nodes.forEach((node) => {
    const match = node.nodeValue.match(pattern);
    const start = match.index + match[1].length;
    const original = match[2];

    const span = document.createElement('span');
    span.setAttribute('data-free-shipping-converted', '');
    span.textContent = `${original} (${converted})`;

    const after = node.splitText(start);
    after.nodeValue = after.nodeValue.slice(original.length);
    node.parentNode.insertBefore(span, after);
  });
}

freeShippingConvertTextAmounts(document.body);
// Sections re-rendered in the theme editor get their text converted again.
document.addEventListener('shopify:section:load', (event) => freeShippingConvertTextAmounts(event.target));

